// Shuriken (currency) calculation utilities
// Shurikens are earned per drill and displayed on the home screen (weekly + total)

// Default reward config - stored in school Firestore doc under rewardConfig
// Schools can override any of these values
export const DEFAULT_REWARD_CONFIG = {
  baseRate: 1, // shurikens per minute of training
  firstDrillBonus: 8, // one-time lifetime bonus for completing very first drill ever
  bonusLadder: [
    { minutes: 5, bonus: 3 },
    { minutes: 10, bonus: 5 },
  ],
  leagueThresholds: {
    lead:     { stay: 0,  promote: 10 },
    bronze:   { stay: 10, promote: 20 },
    silver:   { stay: 20, promote: 35 },
    gold:     { stay: 35, promote: 55 },
    platinum: { stay: 55, promote: 80 },
    dragon:   { stay: 80, promote: null },
  },
  weekStart: { day: 0, timezone: 'Asia/Jerusalem' }, // 0 = Sunday
};

export const LEAGUE_ORDER = ['lead', 'bronze', 'silver', 'gold', 'platinum', 'dragon'];

/**
 * Compute the streak-based multiplier.
 * Day 1 = 1.0x, Day 2 = 1.1x, ..., caps at 2.5x (~day 16)
 */
export function calculateMultiplier(currentStreak) {
  if (!currentStreak || currentStreak <= 0) return 1.0;
  return Math.min(1.0 + Math.max(0, currentStreak - 1) * 0.1, 2.5);
}

/**
 * Calculate shurikens earned for a single drill.
 * Multiplier is applied to duration before the session bonus ladder.
 *
 * @param {number} durationSeconds
 * @param {number} multiplier - from calculateMultiplier()
 * @param {boolean} isFirstEverDrill - triggers the one-time +8 bonus
 * @param {object} rewardConfig - school reward config (falls back to DEFAULT_REWARD_CONFIG)
 * @returns {number} shurikens earned (floored to integer)
 */
export function calculateDrillShurikens(durationSeconds, multiplier = 1.0, isFirstEverDrill = false, rewardConfig = DEFAULT_REWARD_CONFIG) {
  const cfg = { ...DEFAULT_REWARD_CONFIG, ...rewardConfig };
  const durationMinutes = durationSeconds / 60;
  const base = durationMinutes * multiplier * cfg.baseRate;
  const firstBonus = isFirstEverDrill ? cfg.firstDrillBonus : 0;
  return Math.floor(base) + firstBonus;
}

/**
 * Calculate the session-level bonus ladder shurikens.
 * Applied once per session based on cumulative session duration.
 * Multiplier is applied before the ladder bonuses.
 *
 * @param {number} prevSessionSeconds - total session seconds BEFORE this drill
 * @param {number} newSessionSeconds - total session seconds AFTER this drill
 * @param {number} multiplier
 * @param {object} rewardConfig
 * @returns {number} additional bonus shurikens from ladder milestones crossed
 */
export function calculateSessionBonusShurikens(prevSessionSeconds, newSessionSeconds, multiplier = 1.0, rewardConfig = DEFAULT_REWARD_CONFIG) {
  const cfg = { ...DEFAULT_REWARD_CONFIG, ...rewardConfig };
  const ladder = cfg.bonusLadder || [];
  let bonus = 0;

  for (const rung of ladder) {
    const thresholdSeconds = rung.minutes * 60;
    // Did we just cross this threshold?
    if (prevSessionSeconds < thresholdSeconds && newSessionSeconds >= thresholdSeconds) {
      bonus += rung.bonus;
    }
  }

  // Apply multiplier to bonus too (multiplier applies before ladder)
  return Math.floor(bonus * multiplier);
}

/**
 * Determine league based on weekly shurikens and current league.
 * League can go up or down based on weekly performance.
 *
 * @param {number} weeklyShurikens
 * @param {string} currentLeague
 * @param {object} rewardConfig
 * @returns {string} new league key
 */
export function calculateLeague(weeklyShurikens, currentLeague = 'lead', rewardConfig = DEFAULT_REWARD_CONFIG) {
  const cfg = { ...DEFAULT_REWARD_CONFIG, ...rewardConfig };
  const thresholds = cfg.leagueThresholds;
  const currentIdx = LEAGUE_ORDER.indexOf(currentLeague);
  const safeIdx = currentIdx === -1 ? 0 : currentIdx;

  const current = thresholds[LEAGUE_ORDER[safeIdx]];

  // Promote?
  if (current.promote !== null && weeklyShurikens >= current.promote) {
    const nextIdx = Math.min(safeIdx + 1, LEAGUE_ORDER.length - 1);
    return LEAGUE_ORDER[nextIdx];
  }

  // Demote?
  if (weeklyShurikens < current.stay && safeIdx > 0) {
    return LEAGUE_ORDER[safeIdx - 1];
  }

  return LEAGUE_ORDER[safeIdx];
}

/**
 * Get the soft-reset streak value after missing one or more days.
 * Drops to the nearest lower 25-day milestone instead of resetting to 0.
 * Examples: 40→25, 26→25, 24→0, 25→0
 */
export function softResetStreak(currentStreak) {
  return Math.max(0, Math.floor((currentStreak - 1) / 25) * 25);
}

/**
 * League badge image path
 */
export function leagueBadgePath(league) {
  const idx = LEAGUE_ORDER.indexOf(league) + 1;
  if (idx === 0) return '/leagues/1_lead.png';
  return `/leagues/${idx}_${league}.png`;
}
